import { useGet } from "@albertine/api-client";
import type { Supplier } from "@albertine/types";
import {
    type TagCategory,
    TagCategoryToDisplayName,
    type Tag as TagEnum,
} from "@albertine/types";
import { tagDisplayName } from "albertine-shared-web";
import classNames from "classnames";
import React, { useEffect, useMemo } from "react";
import { type CatalogSourceKey, CatalogSources } from "../../common/CatalogCategories";
import {
    CategoriesWithUncategorized,
    categoryDisplayName,
    OfferingCategoryUncategorizedKey,
} from "../../common/types/OfferingCategory";
import { excludeUndefined, onlyUnique, partition } from "../../common/utils/array.util";
import { notUndefined } from "../../common/utils/object.util";
import { isEmpty } from "../../common/utils/string.util";
import { getSelectableTagFilters } from "../../common/utils/tags.util";
import { singularPlural } from "../../common/utils/time.formatted.util";
import {
    type CatalogSearchUrlState,
    useComponentState,
    useDebounce,
} from "../../utils/customHook.util";
import Button from "../Common/Buttons";
import { Flex, FlexInline } from "../Common/Flex";
import Icon from "../Common/Icon";
import InlineTextDelimiter from "../Common/InlineTextDelimiter";
import Input from "../Common/Input";
import Label from "../Common/Label";
import MultiSelectionDropdown from "../Common/MultiSelectionDropdown";
import ModalOverlay from "../Common/Overlay/ModalOverlay";
import Tag from "../Common/Tag";
import GooglePlaceInputField, {
    useGooglePlaceInputRef,
} from "../GooglePlaceInputField/GooglePlaceInputField";
import "./CatalogFilters.css";
import type { GooglePlace, OfferingCategory, Tenant } from "@albertine/types";
import { type VisibilityFilter, VisibilityFilterTextMap } from "@albertine/types";
import {
    type SearchCatalogItemsQuery,
    TenantSchema,
} from "@albertine/types/openapi/lmt";
import { formatTenantName } from "@/common/utils/tenant.util";
import { Label as LabelUILib } from "@/components/ui/label";
import {
    hasAllBoundingBoxParams,
    toCatalogItemsSearchGetParams,
} from "../../common/CatalogItem";
import {
    clearLocationFields,
    googlePlaceToPartialCatalogLocationFilter,
} from "../../common/utils/catalog.util";
import SelectTags, { type TagSelection } from "./SelectTags";
import { TagToJSXElement } from "./TagsWithCategories";

function getNumberOfSecondaryFiltersApplied(
    secondaryFilters: SearchCatalogItemsQuery | undefined,
): number {
    if (!secondaryFilters) {
        return 0;
    }
    const isPublishedApplied = notUndefined(secondaryFilters.published) ? 1 : 0;
    const isSuppliersApplied = secondaryFilters.supplierLmtIds?.length ?? 0;
    const isSourceApplied = (secondaryFilters.sources || []).length;
    const isTagsApplied = notUndefined(secondaryFilters.tags)
        ? (secondaryFilters.tags || []).length
        : 0;
    const isItemFiltersApplied = [
        !!secondaryFilters.albertineVerified,
        !!secondaryFilters.recentlyOpened,
        !!secondaryFilters.missingTranslations,
    ].filter((it) => it).length;
    // discoverableForTenant: undefined = no filter; null or tenant string = filter active
    const isDiscoverableForTenantApplied =
        secondaryFilters.discoverableForTenant !== undefined ? 1 : 0;

    const numberOfFiltersApplied = [
        isPublishedApplied,
        isSuppliersApplied,
        isSourceApplied,
        isTagsApplied,
        isItemFiltersApplied,
        isDiscoverableForTenantApplied,
    ].reduce((sum, count) => sum + count, 0);

    return numberOfFiltersApplied;
}

type SecondaryFilterSelection = {
    id: string;
    name: string;
    type:
        | "visibility-filter"
        | "tenant-filter"
        | "supplier-filter"
        | "source-filter"
        | "tag-filter"
        | "item-filter";
};

type ItemFilterKey = keyof Pick<
    SearchCatalogItemsQuery,
    "albertineVerified" | "recentlyOpened" | "missingTranslations"
>;

type SecondaryFilterSelectionItemSelection = SecondaryFilterSelection & {
    id: ItemFilterKey;
};

function getSecondaryFilterSelections(
    secondaryFilters: SearchCatalogItemsQuery,
    suppliers: Supplier[],
): SecondaryFilterSelection[] {
    const appliedVisiblity = (
        secondaryFilters.published !== undefined ? [secondaryFilters.published] : []
    )
        .map((it) => (it === "true" ? "published" : "unpublished"))
        .map<SecondaryFilterSelection | undefined>((it) =>
            VisibilityFilterTextMap[it]
                ? {
                      id: it,
                      name: VisibilityFilterTextMap[it],
                      type: "visibility-filter",
                  }
                : undefined,
        )
        .filter(excludeUndefined);

    const appliedSuppliers = (secondaryFilters.supplierLmtIds || [])
        .map<SecondaryFilterSelection | undefined>((it) => {
            const supplierDetails = suppliers.find((supplier) => supplier.id === it);

            return supplierDetails
                ? {
                      id: it,
                      name: supplierDetails.name,
                      type: "supplier-filter",
                  }
                : undefined;
        })
        .filter(excludeUndefined);

    const appliedSources = (secondaryFilters.sources || [])
        .map<SecondaryFilterSelection | undefined>((it) =>
            CatalogSources[it as CatalogSourceKey]
                ? {
                      id: it,
                      name: CatalogSources[it as CatalogSourceKey],
                      type: "source-filter",
                  }
                : undefined,
        )
        .filter(excludeUndefined);
    const appliedTags = (secondaryFilters.tags || [])
        .map<SecondaryFilterSelection | undefined>((it) => {
            const displayName = tagDisplayName(it, "en");
            return displayName
                ? {
                      id: it,
                      name: displayName,
                      type: "tag-filter",
                  }
                : undefined;
        })
        .filter(excludeUndefined);

    const albertineVerified: SecondaryFilterSelectionItemSelection = {
        id: "albertineVerified",
        name: "Albertine verified",
        type: "item-filter",
    };
    const recentlyOpened: SecondaryFilterSelectionItemSelection = {
        id: "recentlyOpened",
        name: "Recently opened",
        type: "item-filter",
    };
    const missingTranslations: SecondaryFilterSelectionItemSelection = {
        id: "missingTranslations",
        name: "Missing translations",
        type: "item-filter",
    };
    const appliedItemFilters: SecondaryFilterSelection[] = [
        ...(secondaryFilters.albertineVerified ? [albertineVerified] : []),
        ...(secondaryFilters.recentlyOpened ? [recentlyOpened] : []),
        ...(secondaryFilters.missingTranslations ? [missingTranslations] : []),
    ];

    // Tenant filter: undefined = no filter; null = "Any tenant"; tenant string = specific tenant
    const appliedTenantFilter: SecondaryFilterSelection[] =
        secondaryFilters.discoverableForTenant !== undefined
            ? [
                  {
                      id: secondaryFilters.discoverableForTenant,
                      name:
                          secondaryFilters.discoverableForTenant === "all"
                              ? "All tenants"
                              : formatTenantName(
                                    secondaryFilters.discoverableForTenant,
                                ),
                      type: "tenant-filter",
                  },
              ]
            : [];

    return [
        ...appliedSources,
        ...appliedVisiblity,
        ...appliedTenantFilter,
        ...appliedSuppliers,
        ...appliedTags,
        ...appliedItemFilters,
    ];
}

type Props = {
    className?: string;
    defaultSearch: CatalogSearchUrlState;
    onFilterChange: (queryFilters: CatalogSearchUrlState) => void;
    suppliers: Supplier[];
};

type State = {
    filters: CatalogSearchUrlState;
    isSecondaryFiltersVisible: boolean;
    areSecondaryFiltersActive: boolean;
};

function CatalogFilters(props: Props) {
    const { className, defaultSearch, onFilterChange, suppliers } = props;

    const { state, setState } = useComponentState<State>({
        isSecondaryFiltersVisible: false,
        filters: defaultSearch,
        areSecondaryFiltersActive:
            getNumberOfSecondaryFiltersApplied(defaultSearch.filter) !== 0,
    });
    const debouncedFilters = useDebounce(state.filters, 250);
    const numberOfFiltersApplied = useMemo(
        () => getNumberOfSecondaryFiltersApplied(state.filters.filter),
        [state.filters],
    );

    const googlePlaceInputField = useGooglePlaceInputRef({
        initialValue: state.filters.locationSearchTerm,
    });

    const searchParams = toCatalogItemsSearchGetParams({
        ...defaultSearch.filter,
        pageSize: 1,
        cursor: undefined,
    });
    const { data: searchCatalogData } = useGet("catalogItemsSearch", searchParams);

    useEffect(() => {
        setState({
            areSecondaryFiltersActive:
                getNumberOfSecondaryFiltersApplied(debouncedFilters.filter) !== 0,
        });
        onFilterChange(debouncedFilters);
    }, [debouncedFilters, setState, onFilterChange]);

    function applyFilters(filters: CatalogSearchUrlState) {
        setState({
            areSecondaryFiltersActive:
                getNumberOfSecondaryFiltersApplied(filters.filter) !== 0,
        });
        onFilterChange(filters);
    }

    const activeSecondaryFilters = useMemo(() => {
        if (!state.areSecondaryFiltersActive) {
            return undefined;
        }

        return getSecondaryFilterSelections(state.filters.filter, suppliers);
    }, [state.areSecondaryFiltersActive, state.filters.filter, suppliers]);

    function onKeywordChange(newSearchTerm: string) {
        const newFilters: CatalogSearchUrlState = {
            ...state.filters,
            view: defaultSearch.view,
            filter: {
                ...state.filters.filter,
                searchTerm: newSearchTerm.length !== 0 ? newSearchTerm : undefined,
            },
        };
        setState({
            filters: newFilters,
        });
    }

    function onNameChange(newSearchTerm: string) {
        const newFilters: CatalogSearchUrlState = {
            ...state.filters,
            view: defaultSearch.view,
            filter: {
                ...state.filters.filter,
                nameSearchTerm: newSearchTerm.length !== 0 ? newSearchTerm : undefined,
            },
        };
        setState({
            filters: newFilters,
        });
    }

    function onLocationSelection(place: GooglePlace) {
        const locationSearchTerm = googlePlaceInputField.current?.input
            ? googlePlaceInputField.current?.input()?.value
            : undefined;

        const locationFilterFields = googlePlaceToPartialCatalogLocationFilter(place);

        const newFilters: CatalogSearchUrlState = {
            ...state.filters,
            locationSearchTerm,
            filter: {
                ...clearLocationFields(state.filters.filter),
                ...locationFilterFields,
            },
        };

        setState({
            filters: newFilters,
        });
    }

    function onLocationClear() {
        const newFilters: CatalogSearchUrlState = {
            ...state.filters,
            view: "list",
            locationSearchTerm: undefined,
            filter: clearLocationFields(state.filters.filter),
        };
        setState({
            filters: newFilters,
        });
    }

    function onCategorySelection(newCategories: string[]) {
        const categories =
            newCategories.length === 0
                ? undefined
                : (newCategories as unknown as OfferingCategory[]);

        const newFilters: CatalogSearchUrlState = {
            ...state.filters,
            filter: {
                ...state.filters.filter,
                categories,
            },
        };

        setState({
            filters: newFilters,
        });
    }

    function onSourceSelect(newSelection: { id: CatalogSourceKey; checked: boolean }) {
        const newSelections = newSelection.checked
            ? [...(state.filters.filter.sources || []), newSelection.id]
            : state.filters.filter.sources?.filter((it) => it !== newSelection.id);

        const newFilters: CatalogSearchUrlState = {
            ...state.filters,
            filter: {
                ...state.filters.filter,
                sources: newSelections,
            },
        };
        const newState = {
            filters: newFilters,
        };
        setState(newState);

        return newState;
    }

    function onVisibilitySelection(newSelections: TagSelection<VisibilityFilter>[]) {
        const published = newSelections
            .filter((it) => it.selected)
            .map((it) => (it.id === "published" ? "true" : "false"));
        const newFilters: CatalogSearchUrlState = {
            ...state.filters,
            filter: {
                ...state.filters.filter,
                published: published[0],
            },
        };
        const newState = {
            filters: newFilters,
        };
        setState(newState);
        return newState;
    }

    function onTenantVisibilitySelection(
        newSelections: TagSelection<Tenant | "all">[],
    ) {
        const selected = newSelections.find((it) => it.selected);
        const newFilters: CatalogSearchUrlState = {
            ...state.filters,
            filter: {
                ...state.filters.filter,
                discoverableForTenant: selected?.id,
            },
        };
        const newState = {
            filters: newFilters,
        };
        setState(newState);
        return newState;
    }

    function onTagSelection(tagSelection: TagSelection<TagEnum>[]) {
        const [selected, notSelected] = partition(
            tagSelection,
            (it) => it.selected,
        ).map((selections) => selections.map((it) => it.id));
        const previousTags = state.filters.filter.tags || [];
        const previousExcludingNotSelected = previousTags.filter(
            (it) => !notSelected.includes(it),
        );
        const tags = [...previousExcludingNotSelected, ...selected].filter(onlyUnique);
        const newFilters: CatalogSearchUrlState = {
            ...state.filters,
            filter: {
                ...state.filters.filter,
                tags,
            },
        };
        const newState = {
            filters: newFilters,
        };
        setState(newState);
        return newState;
    }

    function onSupplierSelect(newSelection: { id: string; checked: boolean }) {
        const newSelections = newSelection.checked
            ? [...(state.filters.filter.supplierLmtIds || []), newSelection.id]
            : state.filters.filter.supplierLmtIds?.filter(
                  (it) => it !== newSelection.id,
              );
        const newFilters: CatalogSearchUrlState = {
            ...state.filters,
            filter: {
                ...state.filters.filter,
                supplierLmtIds: newSelections,
            },
        };
        const newState = {
            filters: newFilters,
        };
        setState(newState);

        return newState;
    }

    function onItemFilterSelect(item: ItemFilterKey, checked: boolean) {
        const newFilters: CatalogSearchUrlState = {
            ...state.filters,
            filter: {
                ...state.filters.filter,
                [item]: checked ? "true" : undefined,
            },
        };
        const newState = {
            filters: newFilters,
        };
        setState(newState);

        return newState;
    }

    function clearAllSecondaryFilters() {
        const {
            supplierLmtIds,
            published,
            sources,
            tags,
            albertineVerified,
            recentlyOpened,
            missingTranslations,
            discoverableForTenant,
            ...newFilter
        } = { ...state.filters.filter };

        const newFilters: CatalogSearchUrlState = {
            ...state.filters,
            view: defaultSearch.view,
            filter: newFilter,
        };

        setState({
            filters: newFilters,
            isSecondaryFiltersVisible: false,
        });
        applyFilters(newFilters);
    }

    const hasBounds = hasAllBoundingBoxParams(defaultSearch.filter);

    return (
        <Flex.Row className={classNames("catalog-beta-filters", className)}>
            <Flex.Column gap="medium">
                <Flex.Row gap="large">
                    <Flex.Row
                        gap="medium"
                        alignItems="center"
                        className="catalog-beta-filters__primary"
                    >
                        <div>
                            <Input.PersistentTextField
                                label="Name"
                                placeholder="Search by name"
                                value={defaultSearch.filter.nameSearchTerm || ""}
                                optionToClear
                                onChange={(newSearchTerm) => {
                                    onNameChange(newSearchTerm);
                                }}
                            />
                        </div>

                        <div>
                            <GooglePlaceInputField
                                label="Location"
                                ref={googlePlaceInputField}
                                searchFor="landmarksAndRegions"
                                fields={["id", "addressComponents", "location"]}
                                value={hasBounds ? "Map area" : undefined}
                                onSelect={(place) => {
                                    onLocationSelection(place);
                                }}
                                onClear={() => {
                                    onLocationClear();
                                }}
                            />
                        </div>

                        <div>
                            <MultiSelectionDropdown
                                label="Category"
                                defaultLabel="All"
                                initialValues={state.filters.filter.categories || []}
                                optionList={CategoriesWithUncategorized.options
                                    .filter(
                                        (key) =>
                                            key !== OfferingCategoryUncategorizedKey,
                                    )
                                    .map((key) => ({
                                        value: key,
                                        label: categoryDisplayName(key, "en"),
                                    }))}
                                onChange={(newCategories) => {
                                    onCategorySelection(newCategories);
                                }}
                            />
                        </div>

                        <div>
                            <Input.PersistentTextField
                                label="Keyword"
                                placeholder="Search"
                                value={defaultSearch.filter.searchTerm || ""}
                                optionToClear
                                onChange={(newSearchTerm) => {
                                    onKeywordChange(newSearchTerm);
                                }}
                            />
                        </div>

                        <div>
                            <Flex.Column justifyContent="center">
                                {state.areSecondaryFiltersActive ? (
                                    <Button.Ghost
                                        onClick={() => {
                                            setState({
                                                isSecondaryFiltersVisible: true,
                                            });
                                        }}
                                    >
                                        <Icon.FilterList.Medium.P50 />
                                        <Label.Small.SemiBold.P50>
                                            Filters
                                        </Label.Small.SemiBold.P50>
                                    </Button.Ghost>
                                ) : (
                                    <Button.Ghost
                                        onClick={() => {
                                            setState({
                                                isSecondaryFiltersVisible: true,
                                            });
                                        }}
                                    >
                                        <Icon.FilterList.Medium.N10 />
                                        <Label.Small.SemiBold.N10>
                                            Filters
                                        </Label.Small.SemiBold.N10>
                                    </Button.Ghost>
                                )}
                            </Flex.Column>
                        </div>
                    </Flex.Row>
                </Flex.Row>

                {activeSecondaryFilters && (
                    <Flex.Row
                        gap="small"
                        className="catalog-beta-filters-active-secondary-filters"
                    >
                        {activeSecondaryFilters.map((it) => (
                            <Tag.Medium.Bold.P90 key={it.id}>
                                <FlexInline gap="small">
                                    {it.name}
                                    <Button.Ghost
                                        className="catalog-beta-filters__active-secondary-filter-tag-close"
                                        onClick={() => {
                                            switch (it.type) {
                                                case "source-filter": {
                                                    const newState = onSourceSelect({
                                                        id: it.id as CatalogSourceKey,
                                                        checked: false,
                                                    });
                                                    applyFilters(newState.filters);
                                                    break;
                                                }
                                                case "visibility-filter": {
                                                    const newState =
                                                        onVisibilitySelection([
                                                            {
                                                                id: it.id as VisibilityFilter,
                                                                value: it.name,
                                                                selected: false,
                                                            },
                                                        ]);
                                                    applyFilters(newState.filters);
                                                    break;
                                                }
                                                case "tenant-filter": {
                                                    const newState =
                                                        onTenantVisibilitySelection([
                                                            {
                                                                id: it.id as
                                                                    | Tenant
                                                                    | "all",
                                                                value: it.name,
                                                                selected: false,
                                                            },
                                                        ]);
                                                    applyFilters(newState.filters);
                                                    break;
                                                }
                                                case "supplier-filter": {
                                                    const newState = onSupplierSelect({
                                                        id: it.id,
                                                        checked: false,
                                                    });
                                                    applyFilters(newState.filters);
                                                    break;
                                                }
                                                case "tag-filter": {
                                                    const newState = onTagSelection([
                                                        {
                                                            id: it.id as TagEnum,
                                                            value: it.name,
                                                            selected: false,
                                                        },
                                                    ]);
                                                    applyFilters(newState.filters);
                                                    break;
                                                }
                                                case "item-filter": {
                                                    const newState = onItemFilterSelect(
                                                        it.id as ItemFilterKey,
                                                        false,
                                                    );
                                                    applyFilters(newState.filters);
                                                    break;
                                                }
                                                default:
                                                    break;
                                            }
                                        }}
                                    >
                                        <Icon.Close.XSmall.P50 />
                                    </Button.Ghost>
                                </FlexInline>
                            </Tag.Medium.Bold.P90>
                        ))}
                    </Flex.Row>
                )}
            </Flex.Column>

            {state.isSecondaryFiltersVisible && (
                <ModalOverlay.Base
                    title={
                        <FlexInline gap="medium">
                            <Label.Medium.SemiBold.N10>
                                Filters
                            </Label.Medium.SemiBold.N10>
                            {numberOfFiltersApplied !== 0 && (
                                <span className="catalog-beta-filters__number-of-filters">
                                    {numberOfFiltersApplied}
                                </span>
                            )}
                        </FlexInline>
                    }
                >
                    <ModalOverlay.Content padding="medium">
                        <Flex.Column>
                            <Flex.Row
                                gap="medium"
                                alignItems="start"
                                className="catalog-beta-filters__secondary-filters-section"
                            >
                                <Flex.Column className="catalog-beta-filters__column-left">
                                    <Label.Large.SemiBold>Source</Label.Large.SemiBold>
                                </Flex.Column>

                                <Flex.Column>
                                    <div className="catalog-beta-filters__checkboxes">
                                        {Object.entries(CatalogSources).map(
                                            ([key, value]) => (
                                                <Input.Checkbox
                                                    label={value}
                                                    key={key}
                                                    checked={
                                                        !!(
                                                            state.filters.filter
                                                                .sources || []
                                                        ).includes(key)
                                                    }
                                                    onChange={(checked) => {
                                                        onSourceSelect({
                                                            id: key as CatalogSourceKey,
                                                            checked,
                                                        });
                                                    }}
                                                />
                                            ),
                                        )}
                                    </div>
                                </Flex.Column>
                            </Flex.Row>

                            <Flex.Row
                                gap="medium"
                                alignItems="start"
                                className="catalog-beta-filters__secondary-filters-section"
                            >
                                <Flex.Column className="catalog-beta-filters__column-left">
                                    <Label.Large.SemiBold>
                                        Visibility
                                    </Label.Large.SemiBold>
                                </Flex.Column>

                                <Flex.Column justifyContent="start" gap="small">
                                    <SelectTags<VisibilityFilter>
                                        values={[
                                            {
                                                id: "published",
                                                selected:
                                                    state.filters.filter.published ===
                                                    "true",
                                                value: "Published",
                                            },
                                            {
                                                id: "unpublished",
                                                selected:
                                                    state.filters.filter.published ===
                                                    "false",
                                                value: "Unpublished",
                                            },
                                        ]}
                                        onChange={(newSelections) => {
                                            onVisibilitySelection(newSelections);
                                        }}
                                    />
                                </Flex.Column>
                            </Flex.Row>

                            <Flex.Row
                                gap="medium"
                                alignItems="start"
                                className="catalog-beta-filters__secondary-filters-section"
                            >
                                <Flex.Column className="catalog-beta-filters__column-left">
                                    <Label.Large.SemiBold>Item</Label.Large.SemiBold>
                                </Flex.Column>

                                <Flex.Column>
                                    <div className="catalog-beta-filters__checkboxes">
                                        <Input.Checkbox
                                            label="Albertine verified"
                                            checked={
                                                state.filters.filter
                                                    .albertineVerified === "true"
                                            }
                                            onChange={(checked) => {
                                                onItemFilterSelect(
                                                    "albertineVerified",
                                                    checked,
                                                );
                                            }}
                                        />
                                        <Input.Checkbox
                                            label="Missing translations"
                                            checked={
                                                state.filters.filter
                                                    .missingTranslations === "true"
                                            }
                                            onChange={(checked) => {
                                                onItemFilterSelect(
                                                    "missingTranslations",
                                                    checked,
                                                );
                                            }}
                                        />
                                        <Input.Checkbox
                                            label="Recently opened"
                                            checked={
                                                state.filters.filter.recentlyOpened ===
                                                "true"
                                            }
                                            onChange={(checked) => {
                                                onItemFilterSelect(
                                                    "recentlyOpened",
                                                    checked,
                                                );
                                            }}
                                        />
                                    </div>
                                </Flex.Column>
                            </Flex.Row>

                            <Flex.Row
                                gap="medium"
                                alignItems="start"
                                className="catalog-beta-filters__secondary-filters-section"
                            >
                                <Flex.Column className="catalog-beta-filters__column-left">
                                    <Label.Large.SemiBold>
                                        Supplier
                                    </Label.Large.SemiBold>
                                </Flex.Column>

                                <Flex.Column>
                                    <div className="catalog-beta-filters__checkboxes">
                                        {suppliers.map(({ id, name }) => (
                                            <Input.Checkbox
                                                label={name}
                                                key={id}
                                                checked={
                                                    !!(
                                                        state.filters.filter
                                                            .supplierLmtIds || []
                                                    ).includes(id)
                                                }
                                                onChange={(checked) => {
                                                    onSupplierSelect({
                                                        id,
                                                        checked,
                                                    });
                                                }}
                                            />
                                        ))}
                                    </div>
                                </Flex.Column>
                            </Flex.Row>

                            {Object.entries(
                                getSelectableTagFilters(
                                    state.filters.filter.categories,
                                ),
                            ).map((_tags) => {
                                const tagCategory = _tags[0] as TagCategory;
                                const tagsAvailable = _tags[1];
                                const tagCategoryName =
                                    TagCategoryToDisplayName[tagCategory];
                                const tags = Object.values(tagsAvailable).map(
                                    (tag) => ({
                                        id: tag,
                                        value: tagDisplayName(tag, "en"),
                                        displayValue: TagToJSXElement[tag],
                                        selected: state.filters.filter.tags
                                            ? state.filters.filter.tags.includes(tag)
                                            : false,
                                    }),
                                );

                                return (
                                    <Flex.Row
                                        key={tagCategory}
                                        gap="medium"
                                        alignItems="start"
                                        className="catalog-beta-filters__secondary-filters-section"
                                    >
                                        <Flex.Column className="catalog-beta-filters__column-left">
                                            <Label.Large.SemiBold>
                                                {tagCategoryName}
                                            </Label.Large.SemiBold>
                                        </Flex.Column>

                                        <Flex.Column justifyContent="start">
                                            <SelectTags<TagEnum>
                                                values={tags}
                                                onChange={(newTags) => {
                                                    onTagSelection(newTags);
                                                }}
                                                selectMultiple
                                            />
                                        </Flex.Column>
                                    </Flex.Row>
                                );
                            })}

                            <div className="flex w-full flex-row items-start gap-2 border-border-subtle border-b py-8 last:border-b-0">
                                <div className="catalog-beta-filters__column-left">
                                    <LabelUILib className="font-semibold text-lg">
                                        Tenant
                                    </LabelUILib>
                                </div>

                                <div className="flex w-full flex-col items-start justify-start gap-1">
                                    <SelectTags<Tenant | "all">
                                        values={[
                                            {
                                                id: "all",
                                                selected:
                                                    state.filters.filter
                                                        .discoverableForTenant === null,
                                                value: "Any tenant",
                                            },
                                            ...TenantSchema.options.map((tenant) => ({
                                                id: tenant,
                                                selected:
                                                    state.filters.filter
                                                        .discoverableForTenant ===
                                                    tenant,
                                                value: formatTenantName(tenant),
                                            })),
                                        ]}
                                        onChange={(newSelections) => {
                                            onTenantVisibilitySelection(newSelections);
                                        }}
                                    />
                                </div>
                            </div>
                        </Flex.Column>
                    </ModalOverlay.Content>

                    <ModalOverlay.Footer>
                        <Flex.Row justifyContent="end" gap="medium">
                            <Button.Secondary
                                label="Clear all filters"
                                onClick={() => {
                                    clearAllSecondaryFilters();
                                }}
                            />
                            <Button.Primary
                                label={singularPlural(
                                    `Show ${searchCatalogData?.totalItems ?? 0} result`,
                                    `Show ${searchCatalogData?.totalItems ?? 0} results`,
                                    searchCatalogData?.totalItems ?? 0,
                                )}
                                onClick={() => {
                                    setState({
                                        isSecondaryFiltersVisible: false,
                                    });
                                    applyFilters({
                                        ...state.filters,
                                        view: defaultSearch.view,
                                    });
                                }}
                            />
                        </Flex.Row>
                    </ModalOverlay.Footer>
                </ModalOverlay.Base>
            )}
        </Flex.Row>
    );
}

type MinimizedCatalogFiltersProps = {
    defaultSearch: CatalogSearchUrlState;
    onClick: () => void;
};

export function MinimizedCatalogFilters(props: MinimizedCatalogFiltersProps) {
    const { defaultSearch, onClick } = props;
    const appliedFilters = [
        !isEmpty(defaultSearch.filter.nameSearchTerm)
            ? defaultSearch.filter.nameSearchTerm
            : undefined,
        !isEmpty(defaultSearch.locationSearchTerm)
            ? defaultSearch.locationSearchTerm
            : undefined,
        defaultSearch.filter.categories && defaultSearch.filter.categories.length !== 0
            ? defaultSearch.filter.categories
                  .map((it) => categoryDisplayName(it, "en"))
                  .join(", ")
            : undefined,
    ].filter(excludeUndefined);

    const numberOfFiltersApplied = useMemo(
        () => getNumberOfSecondaryFiltersApplied(defaultSearch.filter),
        [defaultSearch.filter],
    );

    return (
        <Flex.Row gap="medium" className="catalog-filters-minimized" onClick={onClick}>
            <Icon.Search.Medium.N10 />

            <Flex.Row className="catalog-filters-minimized__filter-values">
                {appliedFilters.map((filter, index) => (
                    <React.Fragment key={filter}>
                        <Label.Small.SemiBold.N10>{filter}</Label.Small.SemiBold.N10>
                        {appliedFilters.length - 1 > index && <InlineTextDelimiter />}
                    </React.Fragment>
                ))}

                {appliedFilters.length === 0 && (
                    <Label.Small.SemiBold.N10>Search</Label.Small.SemiBold.N10>
                )}
            </Flex.Row>

            {numberOfFiltersApplied !== 0 && (
                <span className="catalog-beta-filters__number-of-filters">
                    {numberOfFiltersApplied}
                </span>
            )}
        </Flex.Row>
    );
}

export default CatalogFilters;
